import {
  Box,
  Button,
  Divider,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import * as React from 'react';
import {useSaveTagMutation} from '../../hooks/api/tags';
import {TagName} from '../../shared/validation';
import {TagResponse} from '../../shared/tags';

export const TagEditorModal = ({
  tag,
  isOpen,
  onClose,
}: {
  tag?: TagResponse;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const isEditMode = tag !== undefined;
  const saveTagMutation = useSaveTagMutation();
  const {
    formState: {errors, isValid},
    handleSubmit,
    register,
    reset: resetForm,
    watch,
  } = useForm<{tagName: string}>({
    defaultValues: {
      tagName: '',
    },
    values: {tagName: tag?.name ?? ''},
    mode: 'onChange',
  });

  const tagName = watch('tagName');
  const characterCount = tagName.trim().length;

  const handleSaveTagSubmit = (data: {tagName: string}) => {
    if (saveTagMutation.isPending) {
      return;
    }

    saveTagMutation.mutate(
      {
        id: tag?._id,
        name: data.tagName,
        ...(tag?.type === 'dynamic'
          ? {type: 'dynamic', filter: tag.filter}
          : {type: 'static'}),
      },
      {
        onSuccess: handleClose,
      }
    );
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={handleSubmit(handleSaveTagSubmit)}
        rounded="xl"
      >
        <ModalHeader fontWeight="normal" pb="1" pt="6">
          {isEditMode ? 'Edit List Name' : 'Create a new Static List'}
        </ModalHeader>

        {!isEditMode && (
          <ModalHeader
            fontWeight="normal"
            fontSize="sm"
            py="0"
            color="kgray.300"
          >
            Add accounts to this list in My Accounts or Discover.
          </ModalHeader>
        )}
        <ModalCloseButton />
        <ModalBody p="6">
          <FormControl isInvalid={!!errors.tagName}>
            {!isEditMode && (
              <Box
                fontWeight="medium"
                fontSize="xs"
                mb="2"
                textTransform="uppercase"
              >
                List name
              </Box>
            )}
            <Input
              autoComplete="off"
              maxLength={TagName.maxLength}
              placeholder="eg. Mid-Market Customers"
              {...register('tagName', {
                minLength: TagName.minLength,
                maxLength: TagName.maxLength,
                pattern: TagName.pattern,
                required: true,
                setValueAs: (value) => value.trim(),
              })}
            />
            <Box fontSize="xs" color="kgray.300" mt="1">
              {characterCount}/{TagName.maxLength} characters
            </Box>
          </FormControl>
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent="start" gap="4">
          <Button
            colorScheme="kbuttonblue"
            isDisabled={!isValid}
            isLoading={saveTagMutation.isPending}
            type="submit"
          >
            Save
          </Button>
          <Button color="kgray.300" onClick={handleClose} variant="ghost">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
